import { useEffect, useMemo, useRef, useState } from "react";
import DataObjectIcon from "@mui/icons-material/DataObject";
import TableRowsIcon from "@mui/icons-material/TableRows";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { SxProps, Theme } from "@mui/material/styles";
import type {
    GridColDef,
    GridRowId,
    GridRowModel,
    GridRowsProp,
} from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid/DataGrid";
import { useLocalizedStrings } from "../../localization/LocalizedStringsProvider";
import Spacer from "./Spacer";

/* ---------------------------------- types --------------------------------- */

type JsonPrimitive = string | number | boolean;

type KV = {
    id: GridRowId;
    key: string;
    value: JsonPrimitive;
    // Set when the entry is an object or array (e.g. Merchantware's `devices`). The grid is a flat key/value
    // list, so these are shown read-only and edited in the JSON view — but they must survive a table edit.
    nested?: unknown;
};

/* --------------------------------- helpers -------------------------------- */

function safeParse(str: string): Record<string, unknown> {
    try {
        return JSON.parse(str || "{}");
    } catch {
        return {};
    }
}

function objectToRows(obj: Record<string, unknown>): KV[] {
    return Object.entries(obj).map(([k, v]) => {
        if (v !== null && typeof v === "object") {
            return { id: k, key: k, value: JSON.stringify(v), nested: v };
        }
        return {
            id: k, // ✅ stable row id
            key: k,
            value:
                typeof v === "number" || typeof v === "boolean"
                    ? v
                    : String(v ?? ""),
        };
    });
}

function rowsToObject(rows: KV[]): Record<string, unknown> {
    const o: Record<string, unknown> = {};
    for (const r of rows) {
        if (!r.key) continue;
        // Write nested entries back as the original object, not the string shown in the grid — otherwise a
        // table edit silently turns `devices: [...]` into a string and the server rejects the whole config.
        o[r.key] = r.nested !== undefined ? r.nested : r.value;
    }
    return o;
}

// Coerce a grid edit to the type the field already holds, rather than guessing from what it looks like.
// These documents are typed on the server: `softwareVersion` is a string, `cedPort` a number. Inferring the
// type from the text silently corrupts values — "1.0" would become the number 1, a numeric merchantSiteId
// would stop being a string, and a key of "no" would become false. New keys default to string; use the JSON
// view to enter a number or boolean the template didn't already declare.
const toPrimitiveLike = (raw: unknown, previous: unknown): JsonPrimitive => {
    if (typeof raw === "boolean") return raw;

    const s = raw == null ? "" : String(raw);

    if (typeof previous === "number") {
        const n = Number(s.trim());
        return s.trim() !== "" && !Number.isNaN(n) ? n : s;
    }

    if (typeof previous === "boolean") {
        const lower = s.trim().toLowerCase();
        if (lower === "true" || lower === "false") return lower === "true";
        return s;
    }

    return s;
};

function prettyJson(input: string | object, spaces = 2): string {
    const obj = typeof input === "string" ? safeParse(input) : input;
    return JSON.stringify(obj, null, spaces);
}

/* ---------------------------------- props --------------------------------- */

interface JsonEditorProps {
    value: string;
    onChange: (next: string) => void;
    inputRef?: React.Ref<HTMLTextAreaElement>;
    sx?: SxProps<Theme>;
    loading?: boolean;
}

/* -------------------------------- component -------------------------------- */

const JsonEditor = ({
    value,
    onChange,
    inputRef,
    sx,
    loading = false,
}: JsonEditorProps) => {
    const strings = useLocalizedStrings();
    const [view, setView] = useState<"json" | "table">("table");

    const [rows, setRows] = useState<GridRowsProp>(() =>
        objectToRows(safeParse(value))
    );

    // tracks last value emitted by this component to avoid echo-resets
    const lastEmittedRef = useRef<string | null>(null);

    // The JSON view edits this draft, not `value`. Reformatting the prop on every keystroke made the field
    // impossible to type in: any transiently invalid text (the moment you type an opening quote) was dropped
    // and the box snapped back to the last valid document.
    const [draft, setDraft] = useState<string>(() => prettyJson(value, 2));

    const draftError = useMemo(() => {
        try {
            JSON.parse(draft);
            return false;
        } catch {
            return true;
        }
    }, [draft]);

    /* ----------------------- sync only on external changes ----------------------- */

    useEffect(() => {
        if (value !== lastEmittedRef.current) {
            setRows(objectToRows(safeParse(value)));
            setDraft(prettyJson(value, 2));
        }
    }, [value]);

    // Re-seed the draft when switching into the JSON view so it reflects table edits. Deliberately keyed on
    // `view` alone — including `value` would reformat the document mid-keystroke, which is the bug above.
    useEffect(() => {
        if (view === "json") setDraft(prettyJson(value, 2));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    /* --------------------------------- columns -------------------------------- */

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "key",
                headerName: strings.key,
                flex: 1,
                editable: true,
            },
            {
                field: "value",
                headerName: strings.value,
                flex: 2,
                editable: true,
                valueParser: (value, row) =>
                    toPrimitiveLike(value, (row as KV | undefined)?.value),
                valueFormatter: (value) =>
                    typeof value === "boolean"
                        ? String(value)
                        : String(value ?? ""),
            },
        ],
        [strings]
    );

    /* ---------------------------- commit helpers ---------------------------- */

    const commitRowsToParent = (nextRows: GridRowsProp) => {
        setRows(nextRows);

        const obj = rowsToObject(nextRows as KV[]);
        const json = JSON.stringify(obj);

        lastEmittedRef.current = json;
        onChange(json);
    };

    /* ---------------------------------- render --------------------------------- */

    return (
        <Box sx={sx}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={(_, v) => v && setView(v)}
                    aria-label="view"
                >
                    <ToggleButton value="table" aria-label="table">
                        <TableRowsIcon />
                    </ToggleButton>
                    <ToggleButton value="json" aria-label="json">
                        <DataObjectIcon />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Spacer y={2} />

            {view === "table" ? (
                <div style={{ width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        editMode="row"
                        hideFooter
                        loading={loading}
                        // Objects and arrays have no sensible flat editor; they stay visible but are changed
                        // in the JSON view.
                        isCellEditable={(params) =>
                            (params.row as KV).nested === undefined
                        }
                        processRowUpdate={(newRow: GridRowModel) => {
                            const next = rows.map((r) =>
                                r.id === newRow.id ? { ...newRow } : r
                            );
                            commitRowsToParent(next);
                            return newRow;
                        }}
                        onProcessRowUpdateError={(err) => console.error(err)}
                    />
                </div>
            ) : (
                <TextField
                    label={strings.rawConfiguration}
                    value={draft}
                    inputRef={inputRef}
                    disabled={loading}
                    error={draftError}
                    helperText={draftError ? strings.invalidJson : " "}
                    onChange={(e) => {
                        const text = e.target.value;
                        setDraft(text);
                        try {
                            const json = JSON.stringify(JSON.parse(text));

                            lastEmittedRef.current = json;
                            onChange(json);
                        } catch {
                            // Keep the text the user is typing and flag it; the form holds the last valid
                            // document, so a half-typed edit can't be saved.
                        }
                    }}
                    multiline
                    fullWidth
                    InputProps={{
                        sx: {
                            alignItems: "flex-start",
                            height: 480,
                            "& .MuiInputBase-inputMultiline": {
                                height: "100% !important",
                                overflow: "auto",
                            },
                            "& textarea": {
                                fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                                fontVariantLigatures: "none",
                                letterSpacing: 0,
                            },
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default JsonEditor;
